/**
 * Copyright (c) S2 Partnership Ltd. 2023
 *
 * A service that implements Authorisation such as logging in requests and branding/translations.
 *
 * @summary Implements Authorisation requests
 * @author S2 Partnership Ltd
 *
 * Created at     : 01-05-2022
 */
import { Injectable } from '@angular/core';
import { ProcessingOverlayService } from './processing-overlay.service';
import { HttpClient, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { IRiskwiseHttpResponse } from '../../http/api-xml-response-parser';
import { StorageService, StorageKey } from './storage.service';
import { AuthenticatedUser } from '../view-models/authenticated-user';
import { LanguageSelected, LoginData } from '../view-models/login_data';
import { EncryptionService } from './encryption.service';
import { Platform } from '@ionic/angular';
import { Router } from '@angular/router';
import { BrandingService } from './branding.service';
import { translationObject } from 'src/constants';
import { lastValueFrom, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  authorizationKey: string;
  authenticatedUser: AuthenticatedUser;
  credentials: any;
  pauseUTC: number;
  timeout = 0;
  eulaMessage: string;
  mfaToken = '';
  mfaRequiredResponse: boolean;
  /** Stores the default English translations downloaded from the web. */
  englishTranslations: any = null;
  /** Stores the French Canadian transaltions downloaded from the web. */
  frenchCanadianTranslations: any = null;
  /** Subscribes to the language selection saved on the device. */
  languageSubscription: Subscription;
  /** Stores the current language selection saved on the device. */
  currentLanguageSelection: LanguageSelected = null;

  /**
   * Standard Angular Constructor.
   *
   * @ignore
   */
  constructor(
    /* eslint-disable */
    private httpClient: HttpClient,
    private processingOverlayService: ProcessingOverlayService,
    private storageService: StorageService,
    private encryptionService: EncryptionService,
    public platform: Platform,
    private router: Router,
    private brandingService: BrandingService
    /* eslint-enable */
  ) {
    this.platform.pause.subscribe(async () => {
      this.platformPauseEvent();
    });

    this.platform.resume.subscribe(async () => {
      this.platformResumeEvent();
    });
  }

  /**
   * Gets the Authenticated User.
   *
   * @return {any} Authenticated user object.
   */
  getAuthUser(): any {
    return this.authenticatedUser;
  }

  /**
   * Gets the EULA message.
   *
   * @return {any} The EULA message.
   */
  getEulaMessage(): any {
    return this.eulaMessage;
  }

  /**
   * Sets Authenticated User.
   *
   * @arg {any} value User object.
   */
  setAuthUser(value: any) {
    this.authenticatedUser = value;
  }

  /**
   * Pauses UTC.
   */
  platformPauseEvent() {
    this.pauseUTC = Date.now();
  }

  /**
   * Checks if the timeout value has been reached since the app has returned
   * from the background and goes to the login view if it has.
   */
  platformResumeEvent() {
    const nowUTC = Date.now();

    if (this.pauseUTC < (nowUTC - this.timeout * 1000)) {
      this.router.navigate(['/login']);
    }
  }

  /**
   * Sets Timeout.
   *
   * @arg {number} value The to timeout.
   */
  setTimeout(value: number) {
    this.timeout = value;
  }

  /**
   * Sets Authorisation key.
   *
   * @arg {string} value Authorisation key.
   */
  setAuthKey(value: string) {
    this.authorizationKey = value;
  }

  /**
   * Gets the authorisation key.
   *
   * @return {string} Authorization key.
   */
  getAuthKey(): string {
    return this.authorizationKey;
  }

  /**
   * Sequentially runs the requests to get translations and branding for the app.
   *
   * @arg {any} credentials Form value data.
   * @arg {boolean} isForLogin Boolean for checking if it is for login.
   * @return {Promise<any>} Object with sucess / failure.
   */
  async login(credentials?: any, isForLogin = false): Promise<any> {
    if (!credentials) {
      credentials = this.credentials;
    } else {
      this.credentials = credentials;
    }

    const formDataBody = new FormData();
    formDataBody.append('company_id', credentials.companyId);
    formDataBody.append('password', credentials.password);
    formDataBody.append('password_hashed', '0');
    formDataBody.append('username', credentials.username);
    formDataBody.append('version', environment.buildVersion);
    if (this.authenticatedUser !== null) {
      this.credentials.mfa_token = this.authenticatedUser.mfaToken;
      credentials.mfa_token = this.authenticatedUser.mfaToken;
      formDataBody.append('mfa_token', this.authenticatedUser.mfaToken);
    }

    await this.processingOverlayService.display();
    return await lastValueFrom(this.httpClient.post(`/login`, formDataBody, { responseType: 'json', observe: 'response', withCredentials: true })).then(
      async (data: HttpResponse<any>) => {
        const dto: IRiskwiseHttpResponse = data.body;

        credentials.hashedPassword = this.encryptionService.hash(credentials.password);
        this.authenticatedUser = new AuthenticatedUser(
          credentials.companyId,
          credentials.username,
          credentials.hashedPassword,
          !!dto.body.eulaAccepted,
          !!dto.body.mfaRequired,
          !!dto.body.mfaSms,
          !!dto.body.mfaEmail,
          credentials.mfa_token
        );
        if (isForLogin) {
          await this.getClientTranslationsAndBranding(credentials.companyId);
        }
        await this.storageService.set<AuthenticatedUser>(
          StorageKey.AUTHENTICATED_USER,
          this.authenticatedUser
        );

        this.setTimeout(dto.body.timeout);

        await this.storageService.set<LoginData>(
          StorageKey.LOGIN_DATA,
          new LoginData(
            dto.body
          )
        );

        await this.processingOverlayService.dismiss();
        this.eulaMessage = dto.body.eulaMessage;
        this.mfaRequiredResponse = dto.body.mfaRequired;
        return { authUser: this.authenticatedUser, success: true, eulaMessage: dto.body.eulaMessage || '' };
      },
      async (response: HttpErrorResponse) => {
        await this.processingOverlayService.dismiss();
        // TODO Move below line to interceptor (make common/DRY?)
        let translationKey = 'COMMON.NETWORK_FAILURE';
        if (response.status === 401) {
          translationKey = 'PAGES.LOGIN.ERRORS.INVALID_CREDENTIALS';
        } else if (response.status === 400) {
          return { success: false, errorMessage: response.error.message };
        }

        return { success: false, error: translationKey };
      }
    );
  }

  /**
   * Checks the credentials against the last successful login attempt.
   *
   * @arg {any} credentials The entered credentials or saved biometrics credentials to be checked.
   * @return {boolean} True is returned if the credentials match.
   */
  offlineLogin(credentials: any): boolean {
    return (credentials.companyId === this.authenticatedUser.companyId &&
        credentials.username === this.authenticatedUser.username &&
        this.encryptionService.hash(credentials.password) === this.authenticatedUser.hashedPassword);
  }

  /**
   * Sequentially runs the requests to get translations and branding for the app.
   *
   * @arg {string} clientId The ID of the current client system.
   */
  async getClientTranslationsAndBranding(clientId: string) {
    try {
      await this.getTranslations(clientId);
      await this.getBranding(clientId);
    } catch (error) {
      console.log('####' + error);
    }
  }

  /**
   * Sends a web request to get the translations for the current client ID being accessed.
   *
   * @arg {string} clientId The ID of the client being accessed.
   * @return {observable} The observable for the data packet response of the translations request.
   */
  async getTranslations(clientId: string) {
    return await lastValueFrom(this.httpClient.post(`/translations/${clientId}`, null, { responseType: 'json', observe: 'response', withCredentials: true })).then(
      async (data: HttpResponse<any>) => {
        this.englishTranslations = data.body.body.translations.en;
        if (data.body.body.translations.frc) {
          this.frenchCanadianTranslations = data.body.body.translations.frc;
        }

        let translation;
        if (this.currentLanguageSelection && this.currentLanguageSelection.code === 'fr-ca') {
          translation = data.body.body.translations.frc;
        } else {
          translation = data.body.body.translations.en;
        }
        translationObject.currentTranslation = translation;
      },
      async (response: HttpErrorResponse) => {
        console.log('#### failed to get translations for ' + clientId + '. Due to error: ' + response.message);
        let defaultTranslations: any;
        defaultTranslations = require('src/assets/i18n/en.json');
        translationObject.currentTranslation = defaultTranslations;
      }
    );
  }

  /**
   * Gets branding via http request.
   *
   * @arg {string} clientId The client ID.
   */
  async getBranding(clientId: string) {
    return await lastValueFrom(this.httpClient.post(`/branding/${clientId}`, null, { responseType: 'json', observe: 'response', withCredentials: true })).then(
      async (data: HttpResponse<any>) => {
        const body = data.body;
        if (Object.keys(body.body).length > 0) {
          this.brandingService.saveBranding(body.body);
          this.brandingService.setBranding();
        }
      },
      async (response: HttpErrorResponse) => {
        console.log('#### failed to get branding for ' + clientId + '. Due to error: ' + response.message);
      }
    );
  }

  /**
   * Subscribes to and sets the current language selection so it can retrieved later on.
   */
  setLanguageSelection() {
    this.languageSubscription = this.storageService.findItem(StorageKey.LANGUAGE).subject.subscribe(async (language: LanguageSelected) => {
      if (language) {
        this.currentLanguageSelection = language;
      }
    });
  }
}
